import React, { useState, useEffect, useDeferredValue, useMemo } from 'react';
import {
  Box,
  ButtonBase,
  Card,
  Grid,
  Paper,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  styled,
  FormLabel,
  Divider,
  TableSortLabel,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditIcon from '@mui/icons-material/Edit';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ResetPassword from './resetPassword';
import CustomButton from '../../../components/Button/button';
import styles from '../../../assets/styles/usersTable';
import {
  adminSliceActions,
  exportUserApi,
  getusers,
  importUserApi,
  searchUsers,
  sortingTableApi,
} from '../../../redux/slice/adminSlice';
import EditUser from './edituser';
import CustomTextField from '../../../components/TextField/textField';
import { usersField } from '../../../constants';
import closeIcon from '../../../assets/images/close.svg';
import { DeleteForever } from '@mui/icons-material';
import DeleteUser from './deleteUser';
import SubscriptionDetails from './subscriptionDetails';

const CardTitle = styled('div')(() => ({
  fontSize: '1rem',
  fontWeight: '500',
  textTransform: 'capitalize',
  marginBottom: '16px',
  paddingTop: '1rem',
  paddingLeft: '16px',
}));

const TableHeading = styled(TableCell)(() => ({
  fontFamily: 'inherit',
  fontSize: '13px',
}));

const Users = () => {
  const classes = styles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [openPopUp, setOpenPopUp] = useState(false);
  const [openEditPopUp, setOpenEditPopUp] = useState(false);
  const [openDeletePopUp, setOpenDeletePopUp] = useState(false);
  const [openSubscriptionPopUp, setOpenSubscriptionPopUp] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [editUserData, setEditUserData] = useState(null);
  const [deleteUserData, setDeleteUserData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [query, setQuery] = useState('');
  const [orderBy, setOrderBy] = useState('firstName');
  const [order, setorder] = useState<'asc' | 'desc'>('asc');
  const deferredValue = useDeferredValue(query);

  const users = useSelector((state: any) => state.admin.users);
  const totalCount = useSelector((state: any) => state.admin.totalCount);
  const exportusersData = useSelector(
    (state: any) => state.admin.exportUserData
  );
  const paginationOptions = useSelector(
    (state: any) => state.admin.paginationOptions
  );
  const [page, setPage] = useState(paginationOptions.page);
  const [rowsPerPage, setRowsPerPage] = useState(paginationOptions.rows);

  const handlePopUp = () => {
    setOpenPopUp(false);
    setOpenEditPopUp(false);
    setOpenDeletePopUp(false);
    setOpenSubscriptionPopUp(false);
  };

  const onRowsPerPageChange = (event: any) => {
    setRowsPerPage(event.target.value);
    setPage(0);
    dispatch(
      adminSliceActions.updatePaginationOptions({
        page: 0,
        rows: event.target.value,
      })
    );
    dispatch(
      getusers({
        page: 0,
        rowsPerPage: event.target.value,
        order,
        orderBy,
        navigate,
      })
    );
  };
  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
    dispatch(
      adminSliceActions.updatePaginationOptions({
        page: newPage,
        rows: rowsPerPage,
      })
    );
    dispatch(
      getusers({
        page: newPage,
        rowsPerPage,
        deferredValue,
        order,
        orderBy,
        navigate,
      })
    );
  };

  const handleResetPasswordButton = (user: any) => {
    setResetPasswordUser(user);
    setOpenPopUp(true);
  };

  //function for edit user action
  const handleEditUserButton = (user: any) => {
    user === 'Add' && dispatch(adminSliceActions.emptyResetPasswordData());
    setEditUserData(user);
    // setOpenEditPopUp(true);
    setEditUserData((user) => {
      console.log(user);

      setOpenEditPopUp(true);
      return user;
    });
  };

  const handleDeleteUserButton = (user: any) => {
    user === 'Delete' && dispatch(adminSliceActions.emptyResetPasswordData());
    setDeleteUserData(user);
    // setOpenEditPopUp(true);
    setDeleteUserData((user) => {
      console.log(user);

      setOpenDeletePopUp(true);
      return user;
    });
  };

  //function that calls export users api
  const handleExportButton = () => {
    dispatch(exportUserApi(navigate));
  };

  //function that download sample file for import user
  const handleSimpleButton = () => {
    var wnd = window.open(`https://api.b4tax.com.au/sample.xlsx`);
    setTimeout(function () {
      wnd?.close();
    }, 1000);
  };

  //function that makes import users api
  const handleFileChange = (e: any) => {
    const files = e.target.files;

    const formData = new FormData();
    formData.append('file', files[0]);
    dispatch(importUserApi({ formData, navigate }));
    // e.target.value = '';
  };

  const handleSort = (e: any, field: string) => {
    const isAsc = orderBy === field && order === 'asc';
    const type = isAsc ? 'desc' : 'asc';
    setorder(type);
    setOrderBy(field);

    const data = {
      field,
      rows: rowsPerPage,
      page,
      type,
      searchStr: deferredValue,
    };
    dispatch(sortingTableApi({ data, navigate }));
  };

  const getSubscriptionStatus = (subs: any) => {
    if (!subs || subs.length === 0) return 'No Subscription';
    if (subs.some((s: any) => s.plan === 'Active')) return 'Active';
    return 'Inactive';
  };

  const getSortedUsers = useMemo(() => {
    if (orderBy !== 'subscriptionStatus') return users;

    const statusOrder = { 'No Subscription': 0, 'Active': 1, 'Inactive': 2 };
    const sorted = [...users].sort((a, b) => {
      const statusA = getSubscriptionStatus(a.subscriptionDetails || []);
      const statusB = getSubscriptionStatus(b.subscriptionDetails || []);
      const orderA = statusOrder[statusA as keyof typeof statusOrder];
      const orderB = statusOrder[statusB as keyof typeof statusOrder];

      if (order === 'asc') {
        return orderA - orderB;
      } else {
        return orderB - orderA;
      }
    });
    return sorted;
  }, [users, orderBy, order]);

  // const handleSearch = (e: any, type: string) => {
  // 	e.preventDefault();
  // 	if (type === 'cancel') {
  // 		dispatch(searchUsers({ page, rowsPerPage, deferredValue: '' }));
  // 		return;
  // 	}
  // 	if (type === 'search') {
  // 		deferredValue === ''
  // 			? dispatch(getusers({ page, rowsPerPage, deferredValue, navigate }))
  // 			: dispatch(searchUsers({ page, rowsPerPage, deferredValue }));
  // 	}
  // };

  //function for downloading file coming from backend after export users
  const downloadFile = () => {
    window.open(`https://api.b4tax.com.au/exportfiles/users.xlsx`, '_blank');
    dispatch(adminSliceActions.emptyExportUserData());
  };

  useEffect(() => {
    dispatch(searchUsers({ page, rowsPerPage, deferredValue }));

    window.addEventListener('resize', () =>
      setIsDesktop(window.innerWidth > 768)
    );
    exportusersData && downloadFile();
    return () =>
      window.removeEventListener('resize', () =>
        setIsDesktop(window.innerWidth > 768)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportusersData, deferredValue]);

  const handleSubscriptionDetails = (subDetails: any, user: any) => {
    setOpenSubscriptionPopUp(true);
    setSubscriptionData(subDetails);
    setEditUserData(user);
  }

  return (
    <>
      <Box component='main' className={classes.main}>
        <div className={classes.divClass}>
          <Stack spacing={2}>
            <Paper elevation={0}>
              <Card className={classes.card}>
                <div style={{ overflow: isDesktop ? 'none' : 'scroll' }}>
                  <Box className={classes.box}>
                    <Grid className={classes.tableHeading}>
                      <CardTitle className={classes.titleText}>Users</CardTitle>
                    </Grid>
                    <Divider />
                    <div className={classes.titleContainer}>
                      <form
                        className={classes.searchBar}
                      // onSubmit={(e) => handleSearch(e, 'default')}
                      >
                        <div className={classes.searchBarContainer}>
                          <input
                            type='text'
                            placeholder='Search by email or name'
                            className={classes.InputContainer}
                            value={query}
                            onChange={(e) => {
                              setQuery(e.target.value);
                            }}
                          />
                          <div className={classes.searchBarButtonContainer}>
                            {query !== '' && (
                              <button
                                type='button'
                                className={classes.cancelButtonClass}
                                onClick={(e) => {
                                  setQuery('');
                                  // handleSearch(e, 'cancel');
                                }}
                              >
                                <img
                                  src={closeIcon}
                                  width={20}
                                  className='img-class'
                                  alt='close'
                                />
                              </button>
                            )}
                            {/* <button
															type="button"
															className={
																query !== ''
																	? classes.submitButtonClass
																	: `${classes.submitButtonClass} ${classes.customSearchBtnClass}`
															}
															onClick={(e) => handleSearch(e, 'search')}
														>
															<img
																src={magnifyingGlass}
																alt="magnifying glass"
																height={35}
																width={40}
																className={classes.searchIconClass}
															/>
														</button> */}
                          </div>
                        </div>
                      </form>
                      <Stack className={classes.stackContainer}>
                        <Stack
                          spacing={1}
                          direction={isDesktop ? 'row' : 'column'}
                          className={classes.stackClass}
                        >
                          <Stack className={classes.buttonContainer}>
                            <CustomButton
                              color='#fff'
                              className={classes.buttonStyle}
                              onClick={() => handleEditUserButton('Add')}
                            >
                              <SvgIcon fontSize='small'>
                                <PersonAddAltIcon />{' '}
                              </SvgIcon>{' '}
                              <div className={classes.addUserClass}>
                                {' Add User'}
                              </div>
                            </CustomButton>
                            <>
                              <CustomButton
                                color='#fff'
                                className={`${classes.importuttonClass} ${classes.buttonStyle}`}
                              >
                                <FormLabel
                                  htmlFor='file-upload'
                                  className={classes.fileInputLabel}
                                >
                                  <SvgIcon fontSize='small'>
                                    <ArrowDownwardIcon />
                                  </SvgIcon>
                                  Import
                                </FormLabel>
                              </CustomButton>
                              <CustomTextField
                                size='small'
                                id='file-upload'
                                className={classes.fileInput}
                                onChange={handleFileChange}
                                type='file'
                              />
                            </>

                            <CustomButton
                              color='#fff'
                              onClick={handleExportButton}
                              className={classes.buttonStyle}
                            >
                              <SvgIcon fontSize='small'>
                                <ArrowUpwardIcon />
                              </SvgIcon>
                              Export
                            </CustomButton>
                            <CustomButton
                              color='#fff'
                              className={classes.buttonStyle}
                              onClick={handleSimpleButton}
                            >
                              SAMPLE
                            </CustomButton>
                          </Stack>
                        </Stack>
                      </Stack>
                    </div>

                    <Table>
                      <TableHead>
                        <TableRow>
                          {usersField.map((item) =>
                            item.field === 'action' ? (
                              <TableHeading
                                align='left'
                                className={classes.customTableField}
                                key={item.field}
                              >
                                {item.label}
                              </TableHeading>
                            ) : (
                              <TableHeading
                                align='left'
                                key={item.field}
                                sortDirection={
                                  orderBy === item.field ? order : false
                                }
                              >
                                <TableSortLabel
                                  onClick={(e) => handleSort(e, item.field)}
                                  direction={
                                    orderBy === item.field ? order : 'asc'
                                  }
                                  className={classes.sortingLabel}
                                >
                                  {item.label}
                                </TableSortLabel>
                              </TableHeading>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getSortedUsers?.length > 0 &&
                          getSortedUsers?.map((customer: any) => {
                            const subs = customer.subscriptionDetails || [];
                            const subscriptionStatus = getSubscriptionStatus(subs);
                            return (
                              <TableRow hover key={customer.id}>
                                <TableCell align='left'>
                                  <Stack>{customer.firstName}</Stack>
                                </TableCell>
                                <TableCell align='left'>
                                  {customer.lastName}
                                </TableCell>
                                <TableCell align='left'>
                                  {customer.email}
                                </TableCell>
                                <TableCell onClick={() => handleSubscriptionDetails(subs, customer)} align="left">
                                  <span
                                    style={{
                                      color:
                                        subscriptionStatus === 'Active'
                                          ? 'green'
                                          : subscriptionStatus === 'Inactive'
                                            ? 'red'
                                            : 'gray',
                                      fontWeight: 600,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {subscriptionStatus}
                                  </span>
                                </TableCell>
                                <TableCell align='left'>
                                  <ButtonBase
                                    onClick={() =>
                                      handleResetPasswordButton(customer)
                                    }
                                  >
                                    <Box
                                      component='span'
                                      className={classes.actionReset}
                                    >
                                      <Tooltip title='Reset Password'>
                                        <LockResetIcon />
                                      </Tooltip>
                                    </Box>
                                  </ButtonBase>
                                  <ButtonBase
                                    onClick={() =>
                                      handleEditUserButton(customer)
                                    }
                                  >
                                    <Box
                                      component='span'
                                      className={classes.actionEdit}
                                    >
                                      <Tooltip title='Edit User'>
                                        <EditIcon />
                                      </Tooltip>
                                    </Box>
                                  </ButtonBase>
                                  <ButtonBase
                                    onClick={() =>
                                      handleDeleteUserButton(customer)
                                    }
                                  >
                                    <Box
                                      component='span'
                                      className={classes.actionDelete}
                                    >
                                      <Tooltip title='Delete User'>
                                        <DeleteForever />
                                      </Tooltip>
                                    </Box>
                                  </ButtonBase>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </Box>
                </div>
                <TablePagination
                  component='div'
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={onRowsPerPageChange}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  count={totalCount}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </Card>
            </Paper>
          </Stack>
        </div>
      </Box>

      <ResetPassword
        open={openPopUp}
        handlePopUp={handlePopUp}
        user={resetPasswordUser}
      />
      <EditUser
        open={openEditPopUp}
        handlePopUp={handlePopUp}
        page={page}
        rowsPerPage={rowsPerPage}
        user={editUserData}
      />
      <DeleteUser
        open={openDeletePopUp}
        handlePopUp={handlePopUp}
        user={deleteUserData}
        page={page}
        rowsPerPage={rowsPerPage}
        deferredValue={deferredValue}
      />
      <DeleteUser
        open={openDeletePopUp}
        handlePopUp={handlePopUp}
        user={deleteUserData}
        page={page}
        rowsPerPage={rowsPerPage}
        deferredValue={deferredValue}
      />
      <SubscriptionDetails
        open={openSubscriptionPopUp}
        handlePopUp={handlePopUp}
        subsDetails={subscriptionData}
        user={editUserData}
      />
    </>
  );
};

export default Users;
